import React, { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import {
  CASE_STUDY_CLIENT,
  CASE_STUDY_CLIENT_URL,
  CASE_STUDY_HEADLINE_ACCENT,
  CASE_STUDY_HEADLINE_LINE2,
  CASE_STUDY_LEAD,
  CASE_STUDY_METRICS,
  CASE_STUDY_SIDEBAR_META,
  CASE_STUDY_STAMP_LABEL,
  CASE_STUDY_STAMP_META,
} from "./caseStudyContent";

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

const fadeUpVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const ClientLink = ({ children, className = "" }) => (
  <a
    href={CASE_STUDY_CLIENT_URL}
    target="_blank"
    rel="noreferrer"
    className={className}
  >
    {children}
  </a>
);

const CaseStudySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const reduceMotion = useReducedMotion();
  const animateMetrics = reduceMotion ? false : isInView;

  return (
    <section
      id="case-study"
      className="roofing-case-study relative overflow-hidden py-20 sm:py-24 lg:py-28 border-y border-orange-500/15 bg-[#060a12]"
      data-testid="case-study-section"
      ref={ref}
    >
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(249,115,22,0.07)_0%,transparent_45%,rgba(20,184,166,0.05)_100%)] pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(-45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)",
          backgroundSize: "12px 12px",
        }}
      />

      <div className="roofing-case-study-inner max-w-6xl mx-auto px-6 lg:px-8 relative">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <div className="inline-flex items-center gap-3 mb-3">
              <span
                className="inline-block badge-mono text-[11px] sm:text-xs font-bold tracking-[0.35em] text-orange-950 bg-orange-400 px-4 py-2 rounded-sm shadow-lg shadow-orange-500/20 rotate-[-1deg]"
                data-testid="case-study-case-label"
              >
                {CASE_STUDY_STAMP_LABEL}
              </span>
              <span className="hidden sm:inline text-[10px] font-mono text-orange-500/50 tracking-widest">
                {CASE_STUDY_STAMP_META}
              </span>
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight text-white font-sans"
              data-testid="case-study-headline"
            >
              <span className="text-orange-400">{CASE_STUDY_HEADLINE_ACCENT}</span>
              <br />
              {CASE_STUDY_HEADLINE_LINE2}
            </h2>
            <p className="text-base sm:text-lg text-gray-400 font-body leading-relaxed mt-4 max-w-2xl">
              {CASE_STUDY_LEAD.beforeLink}
              <ClientLink className="text-white font-semibold hover:text-orange-400 transition-colors">
                {CASE_STUDY_CLIENT.name}
              </ClientLink>
              {CASE_STUDY_LEAD.afterLink}
            </p>
          </div>
          <p className="text-xs font-mono text-gray-500 sm:text-right max-w-[14rem] sm:pb-1">
            {CASE_STUDY_SIDEBAR_META}
          </p>
        </div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4"
          variants={staggerContainer}
          initial="hidden"
          animate={animateMetrics ? "visible" : "hidden"}
        >
          {CASE_STUDY_METRICS.map((metric, index) => (
            <motion.div
              key={metric.label}
              variants={reduceMotion ? undefined : fadeUpVariants}
              className={`relative rounded-xl p-5 sm:p-6 border ${
                metric.highlight
                  ? "border-orange-500/40 bg-gradient-to-br from-orange-500/15 via-orange-500/5 to-transparent ring-1 ring-orange-500/20"
                  : "border-white/[0.08] bg-white/[0.02]"
              }`}
              data-testid={index === 0 ? "case-study-stat" : undefined}
            >
              {metric.highlight && (
                <span className="absolute -top-2.5 left-4 badge-mono text-[9px] text-orange-400 bg-[#060a12] border border-orange-500/30 px-2 py-0.5 rounded-full">
                  KEY RESULT
                </span>
              )}
              <div
                className={`text-4xl sm:text-5xl font-bold font-mono mb-1 leading-none ${
                  metric.highlight ? "text-orange-400" : "text-teal-400"
                }`}
              >
                {metric.value}
              </div>
              <div className="text-sm font-semibold text-white mb-0.5">{metric.label}</div>
              <div className="text-[11px] font-mono text-gray-500">{metric.sub}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default CaseStudySection;
